import { useCallback } from "react";

import { AuthScope } from "apis/dataV2/authTypes";
import { useLazyCheckPermissionsQuery } from "apis/dataV2/dataV2Api";
import { Permission } from "apis/dataV2/permissions";
import { useAppStore } from "app/redux-store-interactions";
import { selectIsOnline } from "slices/explorer";
import { telemetry } from "telemetry";
import { getManualCache } from "utils/manualCache";
import { jsonResponseForCache } from "utils/misc";

export function useCachedCheckPermissionsQuery() {
    const [checkPermissions] = useLazyCheckPermissionsQuery();
    const store = useAppStore();

    return useCallback(
        /**
         * `capture`: reads the captured answer to the open scene's permission check, or `undefined`
         * when there is none for the version loaded. It is read offline, before the manual cache.
         */
        async (
            args: { scope: AuthScope; permissions: Permission[] },
            { capture }: { capture?: () => Promise<Permission[] | undefined> } = {},
        ) => {
            const isOnline = selectIsOnline(store.getState());
            const cacheKey = `/derived/project-permissions/${JSON.stringify(args.scope)}`;
            const cache = await getManualCache();

            if (isOnline) {
                // Network first
                try {
                    const resp = await checkPermissions(args).unwrap();
                    cache.put(cacheKey, jsonResponseForCache(resp));
                    return resp;
                } catch (ex) {
                    console.error(ex);
                    telemetry.trackCaughtException({
                        source: "render.useCachedCheckPermissionsQuery.checkPermissions",
                        category: "init",
                        error: ex,
                        severity: "error",
                    });
                    return await loadFromCache();
                }
            } else {
                return (await capture?.()) ?? (await loadFromCache());
            }

            async function loadFromCache() {
                const resp = await cache.match(cacheKey);
                if (resp) {
                    return await resp.json();
                } else {
                    throw new Error("No cached value for check-permissions");
                }
            }
        },
        [checkPermissions, store],
    );
}
